import {
  useWSSnackbar,
  WSCard,
  WSFlexBox,
  WSPanel,
  WSText
} from "@wingspanhq/fe-component-library";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import { CashbackRedemption } from "@wingspanhq/wingspan-api-sdk";
import { useHistory } from "react-router-dom";
import { cashbackApi } from "@api";
import {
  queriesCashback,
  queriesInternalAccounts,
  queriesTransactions
} from "@query";
import { IdempotencyKey } from "@utils/idempotencyKey";
import { useWSMutation } from "@ws-react-query";
import { InfoTooltip } from "../../../shared/components/InfoTooltip/InfoTooltip";
import { TextBulleted } from "../../../shared/components/TextBulleted";
import { selectorActiveBankingAccount } from "../../../shared/selectors/selectorActiveBankingAccount";
import { selectorAccountBalance } from "../../../shared/selectors/selectorAccountBalance";
import { WSServiceError } from "../../../utils/serviceHelper";
import { useCashbackDetailsModal } from "../modals/useCashbackDetailsModal";

const useMutationRedeemCashbackBalance = () =>
  useWSMutation<CashbackRedemption, WSServiceError, { amount?: number }>(
    async request => {
      const cashbackBalance = await cashbackApi.getCashbackBalance({});
      const redeemAmount = request.amount ?? Number(cashbackBalance.balance);

      // Stable across retries of the same redemption so a replay after a
      // client-side timeout is deduped server-side instead of paying out twice.
      const idempotencyKey = new IdempotencyKey({
        amount: String(redeemAmount)
      });

      const redeemResponse = await cashbackApi.redeemCashback({
        idempotencyKey: idempotencyKey.value,
        cashbackRedeemRequest: { amount: redeemAmount }
      });

      idempotencyKey.destroy();

      // Optimistically bump the wallet balance (the v3 banking internal-account
      // balance) so the redeemed cashback shows immediately. The base-key
      // invalidation below reconciles with the server afterwards.
      const bankingAccount = queriesInternalAccounts.listInternalAccounts
        .getQueryData(undefined)
        ?.data?.find(selectorActiveBankingAccount);

      if (bankingAccount) {
        const preRedeemBalance =
          queriesInternalAccounts.getInternalAccountBalance.getQueryData({
            internalAccountId: bankingAccount.id
          });

        if (preRedeemBalance) {
          queriesInternalAccounts.getInternalAccountBalance.setQueryData(
            { internalAccountId: bankingAccount.id },
            {
              ...preRedeemBalance,
              available: Number(preRedeemBalance.available ?? 0) + redeemAmount,
              balance: Number(preRedeemBalance.balance ?? 0) + redeemAmount
            }
          );
        }
      }

      return redeemResponse;
    },
    {
      dependencies: [
        queriesCashback.getCashbackBalance.queryKeyBase,
        // The redemption credits the wallet, so refresh both ledgers that show
        // it: the internal-account feed the Wallet renders, and the bookkeeping
        // transaction feed (the v3 twin of the v1 key this replaced).
        queriesInternalAccounts.listInternalAccountTransactions.queryKeyBase,
        queriesTransactions.listTransactions.queryKeyBase,
        queriesInternalAccounts.getInternalAccountBalance.queryKeyBase
      ]
    }
  );

export const CashbackBalance: React.FC = () => {
  const history = useHistory();
  const queryInternalAccounts =
    queriesInternalAccounts.listInternalAccounts.useQuery();
  const internalAccount = queryInternalAccounts.data?.data?.find(
    selectorActiveBankingAccount
  );
  const queryInternalAccountBalance =
    queriesInternalAccounts.getInternalAccountBalance.useQuery(
      { internalAccountId: internalAccount?.id! },
      {
        enabled: !!internalAccount?.id
      }
    );
  const balance = selectorAccountBalance(queryInternalAccountBalance.data);
  const cashbackDetailsModal = useCashbackDetailsModal();
  const queryCashbackBalance = queriesCashback.getCashbackBalance.useQuery();

  const [redeemCashbackBalance] = useMutationRedeemCashbackBalance();

  const cashbackBalance = Number(queryCashbackBalance.data?.balance) || 0;
  const currentBalance = Number(queryCashbackBalance.data?.balance) || 0;

  const pendingBalance = Number(queryCashbackBalance.data?.pendingBalance) || 0;

  const availableBalance = balance.available;

  const { openSnackbar } = useWSSnackbar();

  const isLoading = queryCashbackBalance.isInitialLoading;

  const showWarning =
    !isLoading && (availableBalance < 100 || pendingBalance === 0);

  return (
    <WSCard
      shimmer={isLoading}
      fullHeight
      header={{
        value: {
          action: {
            label: "Details",
            rightIcon: "chevron-right",
            onClick: () => {
              cashbackDetailsModal.open();
            }
          }
        },
        label: {
          text: "Cashback balance"
        }
      }}
      divider
      actions={{
        alignment: "fill",
        sortButtons: false,
        buttons: [
          {
            label: "Redeem",
            kind: "Secondary",
            disabled: !cashbackBalance,
            onAsyncClick: async () => {
              await redeemCashbackBalance(
                {},
                {
                  onSuccess() {
                    openSnackbar({
                      type: "success",
                      message: "Cashback successfully sent!"
                    });
                  },
                  onError() {
                    openSnackbar({
                      type: "warning",
                      message: "Failed to send cashback!"
                    });
                  }
                }
              );
            }
          }
          /*
          {
            label: "View transactions",
            kind: "Link",
            disabled: !cashbackBalance,
            rightIcon: "chevron-right",
            onClick: () => {
              history.push("/member/wallet/transactions");
            }
          }
          */
        ]
      }}
    >
      <WSText.Heading1
        weight="book"
        color={cashbackBalance > 0 ? "green500" : "gray200"}
        mb="XL"
      >
        {formatMoney(cashbackBalance, balance.currency)}
      </WSText.Heading1>

      <WSFlexBox.CenterY justify="space-between" mb="M">
        <WSText.ParagraphSm color="gray500">Total balance</WSText.ParagraphSm>
        <WSText.ParagraphSm color="gray600">
          {formatMoney(currentBalance, balance.currency)}
        </WSText.ParagraphSm>
      </WSFlexBox.CenterY>
      <WSFlexBox.CenterY justify="space-between">
        <WSFlexBox.CenterY>
          <WSText.ParagraphSm color="gray500" mr="S">
            Pending amounts
          </WSText.ParagraphSm>
          <InfoTooltip
            text={
              <>
                Cashback: pending amounts
                <TextBulleted>
                  <li>
                    Pending amounts from eligible purchases during the current
                    statement period.
                  </li>
                  <li>
                    Maintain $100+ average daily balance in your Wingspan Wallet
                    to earn cash back.
                  </li>
                  <li>
                    Returned or canceled purchases will reduce the eligible
                    cashback amounts.
                  </li>
                  <li>
                    Cashback monthly total posts to your account 5 business days
                    after each monthly cycle closes when all transactions are
                    settled in full.
                  </li>
                </TextBulleted>
              </>
            }
          />
        </WSFlexBox.CenterY>
        <WSText.ParagraphSm color="gray500">
          {formatMoney(pendingBalance, balance.currency)}
        </WSText.ParagraphSm>
      </WSFlexBox.CenterY>
      {/*<WSText.ParagraphXs color="gray500">
        Period ending {"TODO"}
      </WSText.ParagraphXs>*/}
      {showWarning ? (
        <WSPanel noBorder colorBackground="amber50" mt="L" p="M">
          <WSText.ParagraphXs color="gray600">
            You’re missing out on cardholder benefits! To earn cashback—use your
            card on eligible purchases every month and keep your average daily
            balance above $100.
          </WSText.ParagraphXs>
        </WSPanel>
      ) : null}
    </WSCard>
  );
};
